import { Signal } from 'typed-signals';
import type { AppStateController } from '../state/AppStateController';
import { BoosterType } from '../../game/boosters/types';
import { GAME_ECONOMY_SETTINGS } from '../../game/GameConstants';
import { getLivesCoinCost } from '../state/lives/getLivesCoinCost';
import type { ResourceMap } from './types';
import { boosterToInventoryMap, InventoryItem, TransactionReason } from './types';
import type { ResourceTrackingProps } from './ResourceTracker';

const itemGettersAndSetters: Record<
  InventoryItem,
  { getAmount: (state: AppStateController) => number; setAmount: (state: AppStateController, value: number) => void }
> = {
  [InventoryItem.Coin]: {
    getAmount: (state) => state.getCoins(),
    setAmount: (state, value) => {
      state.setCoins(value);
    },
  },
  [InventoryItem.Lives]: {
    getAmount: (state) => state.getLives(),
    setAmount: (state, value) => {
      state.setLives(value);
    },
  },
  [InventoryItem.UnlimitedLivesMinutes]: {
    getAmount: (state) => state.getUnlimitedLivesMinutes(),
    setAmount: (state, value) => {
      state.setUnlimitedLivesMinutes(value);
    },
  },
  [InventoryItem.BoosterHammer]: {
    getAmount: (state) => state.getBoosterInventoryAmount(BoosterType.Hammer),
    setAmount: (state, value) => {
      state.setBoosterInventoryAmount(BoosterType.Hammer, value);
    },
  },
  [InventoryItem.BoosterMove]: {
    getAmount: (state) => state.getBoosterInventoryAmount(BoosterType.Move),
    setAmount: (state, value) => {
      state.setBoosterInventoryAmount(BoosterType.Move, value);
    },
  },
  [InventoryItem.BoosterRedraw]: {
    getAmount: (state) => state.getBoosterInventoryAmount(BoosterType.Redraw),
    setAmount: (state, value) => {
      state.setBoosterInventoryAmount(BoosterType.Redraw, value);
    },
  },
  [InventoryItem.BoosterUndo]: {
    getAmount: (state) => state.getBoosterInventoryAmount(BoosterType.Undo),
    setAmount: (state, value) => {
      state.setBoosterInventoryAmount(BoosterType.Undo, value);
    },
  },
  [InventoryItem.FreeSpin]: {
    getAmount: (state) => state.getFreeSpins(),
    setAmount: (state, value) => {
      state.setFreeSpins(value);
    },
  },
};

export class ResourceController {
  public readonly onTransactionMade = new Signal<
    (change: ResourceMap, reason: TransactionReason, trackingProps: ResourceTrackingProps) => void
  >();

  constructor(private readonly state: AppStateController) {}

  public addLevelWinReward(coins: number): void {
    this.makeTransaction(
      {
        [InventoryItem.Coin]: coins,
      },
      TransactionReason.LevelWinReward,
    );
  }

  public canAfford(amount: number): boolean {
    return this.state.canAffordCoinCost(amount);
  }

  public addDailyReward(reward: ResourceMap): void {
    this.makeTransaction(reward, TransactionReason.DailyReward);
  }

  public addWinStreakReward(reward: ResourceMap, gameEventId: string): void {
    this.makeTransaction(reward, TransactionReason.WinStreakReward, { gameEventId });
  }

  public addLevelWinDoubledReward(coins: number): void {
    this.makeTransaction(
      {
        [InventoryItem.Coin]: coins,
      },
      TransactionReason.LevelWinDoubleReward,
    );
  }

  public payForRevive(cost: number = GAME_ECONOMY_SETTINGS.reviveCoinCost): void {
    this.makeTransaction(
      {
        [InventoryItem.Coin]: -cost,
      },
      TransactionReason.BuyRevive,
    );
  }

  public useLife(): void {
    this.makeTransaction(
      {
        [InventoryItem.Lives]: -1,
      },
      TransactionReason.UseLife,
    );
  }

  public payForLivesRefill(lives: number): void {
    this.makeTransaction(
      {
        [InventoryItem.Lives]: lives,
        [InventoryItem.Coin]: -getLivesCoinCost(),
      },
      TransactionReason.BuyLives,
    );
  }

  public addLifeQuestReward(): void {
    this.makeTransaction(
      {
        [InventoryItem.Lives]: 1,
      },
      TransactionReason.LifeRequestReward,
    );
  }

  public useBooster(boosterType: BoosterType): void {
    this.makeTransaction(
      {
        [boosterToInventoryMap[boosterType]]: -1,
      },
      TransactionReason.UseBooster,
    );
  }

  public buyBooster(
    boosterType: BoosterType,
    price: number = GAME_ECONOMY_SETTINGS.boosters[boosterType].coinCost,
  ): void {
    this.makeTransaction(
      {
        [boosterToInventoryMap[boosterType]]: 1,
        [InventoryItem.Coin]: -price,
      },
      TransactionReason.BuyBooster,
    );
  }

  public payForCoinBlockerRemoval(price: number): void {
    this.makeTransaction(
      {
        [InventoryItem.Coin]: -price,
      },
      TransactionReason.CoinBlocker,
    );
  }

  public canAffordBooster(boosterType: BoosterType): boolean {
    const price = GAME_ECONOMY_SETTINGS.boosters[boosterType].coinCost;
    return this.state.canAffordCoinCost(price);
  }

  public addInAppPurchase(
    resources: ResourceMap,
    reason: TransactionReason.ShopPurchase | TransactionReason.LevelFailPopupPurchase,
    transactionId?: string,
  ): void {
    this.makeTransaction(resources, reason, { transactionId });
  }

  public makeTransaction(
    change: ResourceMap,
    reason: TransactionReason,
    trackingProps: ResourceTrackingProps = {},
  ): void {
    this.applyChangesToState(change);
    this.onTransactionMade.emit(change, reason, trackingProps);
  }

  private applyChangesToState(change: ResourceMap): void {
    Object.entries(change).forEach(([item, amount]) => {
      const { getAmount, setAmount } = itemGettersAndSetters[item as InventoryItem];
      setAmount(this.state, Math.max(0, getAmount(this.state) + amount));
    });
  }

  public getInventory(): Record<InventoryItem, number> {
    return Object.fromEntries(
      Object.entries(itemGettersAndSetters).map(([key, getterAndSetter]) => [
        key,
        getterAndSetter.getAmount(this.state),
      ]),
    ) as Record<InventoryItem, number>;
  }

  public addRaceEventReward(resourceMap: ResourceMap, gameEventId: string): void {
    this.makeTransaction(resourceMap, TransactionReason.RaceEventReward, { gameEventId });
  }

  public addDioramaCompletedReward(reward: ResourceMap): void {
    this.makeTransaction(reward, TransactionReason.DioramaCompleteReward);
  }

  public addShopFreeGiftReward(resourceMap: ResourceMap): void {
    this.makeTransaction(resourceMap, TransactionReason.ShopFreeGift);
  }

  public addLuckyWheelReward(reward: ResourceMap, isAdSpin: boolean): void {
    this.makeTransaction(
      reward,
      isAdSpin ? TransactionReason.LuckyWheelAdSpinReward : TransactionReason.LuckyWheelFreeSpinReward,
    );
  }

  public useRewardedLuckyWheelFreeSpin(): void {
    this.makeTransaction(
      {
        [InventoryItem.FreeSpin]: -1,
      },
      TransactionReason.UseRewardedLuckyWheelFreeSpin,
    );
  }
}
